import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import Calendar from 'react-calendar';
import { format, isSameDay, parseISO } from 'date-fns';
import 'react-calendar/dist/Calendar.css';
import styles from './CampoAgendamento.module.css';

type Slot = { datetime: string; slotId?: string };

type Props = {
    token: string;
    campoNome: string;
    /** datetime ISO já escolhido (valores[campo.nome]). */
    value?: string;
    /** Grava o horário escolhido: datetime + id opaco do slot. */
    onSelect: (datetime: string, slotId?: string) => void;
};

const chaveDia = (iso: string) => format(parseISO(iso), 'yyyy-MM-dd');

const CampoAgendamento = ({ token, campoNome, value, onSelect }: Props) => {
    const [slots, setSlots] = useState<Slot[]>([]);
    const [estado, setEstado] = useState<'carregando' | 'ok' | 'erro'>('carregando');
    const [diaSelecionado, setDiaSelecionado] = useState<Date | null>(
        value ? parseISO(value) : null
    );

    useEffect(() => {
        let cancelado = false;
        (async () => {
            try {
                const res = await axios.get(
                    `/api/formulario-publico/${token}/agenda`,
                    { params: { campo: campoNome } }
                );
                if (cancelado) return;
                setSlots(res.data?.slots ?? []);
                setEstado('ok');
            } catch {
                if (cancelado) return;
                setEstado('erro');
            }
        })();
        return () => {
            cancelado = true;
        };
    }, [token, campoNome]);

    // Agrupa por dia na timezone do browser (consistente com o calendário).
    const porDia = useMemo(() => {
        const mapa = new Map<string, Slot[]>();
        for (const slot of slots) {
            const dia = chaveDia(slot.datetime);
            if (!mapa.has(dia)) mapa.set(dia, []);
            mapa.get(dia)!.push(slot);
        }
        return mapa;
    }, [slots]);

    const horariosDoDia = useMemo(() => {
        if (!diaSelecionado) return [];
        return porDia.get(format(diaSelecionado, 'yyyy-MM-dd')) ?? [];
    }, [diaSelecionado, porDia]);

    if (estado === 'carregando') {
        return <p className={styles.aviso}>Carregando horários...</p>;
    }
    if (estado === 'erro') {
        return <p className={styles.erroCarga}>Não foi possível carregar os horários. Tente novamente.</p>;
    }
    if (slots.length === 0) {
        return <p className={styles.aviso}>Nenhum horário disponível no momento.</p>;
    }

    return (
        <div className={styles.container}>
            <Calendar
                onChange={(v) => {
                    const d = Array.isArray(v) ? v[0] : v;
                    if (d instanceof Date) setDiaSelecionado(d);
                }}
                value={diaSelecionado}
                tileDisabled={({ date, view }) =>
                    view === 'month' && !porDia.has(format(date, 'yyyy-MM-dd'))
                }
                minDate={new Date()}
            />

            {diaSelecionado && (
                <div className={styles.horarios}>
                    {horariosDoDia.map((slot) => {
                        const selecionado = value === slot.datetime;
                        return (
                            <button
                                type="button"
                                key={slot.datetime}
                                className={`${styles.slot} ${selecionado ? styles.slotSelecionado : ''}`}
                                onClick={() => onSelect(slot.datetime, slot.slotId)}
                            >
                                {format(parseISO(slot.datetime), 'HH:mm')}
                            </button>
                        );
                    })}
                    {horariosDoDia.length === 0 && (
                        <p className={styles.aviso}>Sem horários neste dia.</p>
                    )}
                </div>
            )}

            {value && (
                <p className={styles.resumo}>
                    Escolhido: {format(parseISO(value), "dd/MM/yyyy 'às' HH:mm")}
                </p>
            )}
        </div>
    );
};

export default CampoAgendamento;
